import { ChartConfiguration } from 'chart.js';

export const REPORT_CHART_PRIMARY_COLOR = '#2872e1';
export const REPORT_CHART_PRIMARY_COLOR_UPPER = '#2872E1';

export const REPORT_SENTIMENT_COLORS = {
	negative: '#F7004F',
	positive: '#56E0AE',
	neutral: '#D8DDE7',
	neutralPie: '#E5E7EB'
};

export const REPORT_EMOTION_ROWS = [
	{ key: 'love', label: 'Love', color: '#FF857A' },
	{ key: 'joy', label: 'Joy', color: '#FFA407' },
	{ key: 'surprise', label: 'Surprise', color: '#7F75FF' },
	{ key: 'anger', label: 'Anger', color: '#FF5A1F' },
	{ key: 'sadness', label: 'Sadness', color: '#7AA4FF' },
	{ key: 'fear', label: 'Fear', color: '#2B3742' },
	{ key: 'disgust', label: 'Disgust', color: '#7A4A20' }
];

export const REPORT_PLATFORM_COLORS = [
	REPORT_CHART_PRIMARY_COLOR_UPPER,
	'#4CC9F0',
	'#7A66FF',
	'#FA8072',
	'#FFA407',
	'#56E0AE',
	REPORT_SENTIMENT_COLORS.negative
];

export const REPORT_LANGUAGE_COLORS = [
	REPORT_CHART_PRIMARY_COLOR_UPPER,
	'#4CC9F0',
	'#7A66FF',
	'#FA8072',
	'#FFA407',
	'#56E0AE',
	'#F7004F',
	'#2B3742'
];

export interface ReportChartPoint {
	timestamp: string;
	value: number;
}

export interface ReportChartSeries {
	label: string;
	color: string;
	data: ReportChartPoint[];
}

export interface ReportCategoricalDataset {
	label: string;
	color: string;
	data: number[];
}

export function formatCompactNumber(value: number): string {
	if (!Number.isFinite(value)) {
		return '0';
	}

	const absoluteValue = Math.abs(value);

	if (absoluteValue >= 1000000000) {
		return `${(value / 1000000000).toFixed(
			absoluteValue >= 10000000000 ? 0 : 1
		)}B`;
	}

	if (absoluteValue >= 1000000) {
		return `${(value / 1000000).toFixed(
			absoluteValue >= 10000000 ? 0 : 1
		)}M`;
	}

	if (absoluteValue >= 1000) {
		return `${(value / 1000).toFixed(absoluteValue >= 10000 ? 0 : 1)}K`;
	}

	return `${Math.round(value)}`;
}

export function formatTimestampLabel(timestamp: string): string {
	const d = new Date(timestamp);
	return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

export function formatTimelineTickLabel(
	index: number,
	labels: string[]
): string {
	return [6, 13, 20, 27].includes(index) ? labels[index] ?? '' : '';
}

export function buildPreviewTimeSeriesChartConfig(
	series: ReportChartSeries[],
	type: 'line' | 'area' | 'bar' = 'line',
	options: {
		labels?: string[];
		maxValue?: number;
		timelineTicks?: boolean;
		yStepSize?: number;
	} = {}
): ChartConfiguration<'line' | 'bar'> {
	const labels =
		options.labels ??
		series[0]?.data.map(d => formatTimestampLabel(d.timestamp)) ??
		[];
	const maxValue =
		options.maxValue ??
		Math.ceil(
			Math.max(
				1,
				...series.flatMap(s => s.data.map(point => point.value))
			) * 1.12
		);

	return {
		type: type === 'bar' ? 'bar' : 'line',
		data: {
			labels,
			datasets: series.map(s => ({
				label: s.label,
				data: s.data.map(d => d.value),
				borderColor: s.color,
				backgroundColor:
					type === 'area'
						? `${s.color}59`
						: type === 'bar'
						? s.color
						: `${s.color}14`,
				fill: type === 'area',
				tension: 0.36,
				borderWidth: type === 'bar' ? 0 : 4,
				pointRadius: 0,
				pointHoverRadius: 4,
				pointHoverBackgroundColor: s.color,
				pointHoverBorderColor: '#fff',
				pointHoverBorderWidth: 2,
				borderRadius: type === 'bar' ? 0 : undefined,
				borderSkipped: type === 'bar' ? false : undefined
			}))
		},
		options: {
			responsive: true,
			maintainAspectRatio: false,
			interaction: { mode: 'index', intersect: false },
			plugins: {
				legend: { display: false },
				tooltip: { displayColors: false }
			},
			scales: {
				x: {
					grid: {
						color: 'rgba(141, 166, 182, 0.24)',
						drawBorder: false
					} as any,
					border: { display: false },
					ticks: {
						color: '#8DA6B6',
						autoSkip: !options.timelineTicks,
						maxRotation: 0,
						font: { family: 'Gilroy', size: 11 },
						callback: options.timelineTicks
							? (_: unknown, index: number) =>
									formatTimelineTickLabel(index, labels)
							: undefined
					}
				},
				y: {
					beginAtZero: true,
					suggestedMax: maxValue,
					grid: {
						color: 'rgba(141, 166, 182, 0.24)',
						borderDash: [4, 4],
						drawBorder: false
					} as any,
					border: { display: false },
					ticks: {
						stepSize: options.yStepSize,
						color: '#8DA6B6',
						font: { family: 'Gilroy', size: 11 },
						callback: (value: string | number) =>
							formatCompactNumber(Number(value))
					}
				}
			},
			animation: false
		}
	};
}

export function buildPreviewPieChartConfig(
	labels: string[],
	values: number[],
	colors: string[],
	options: {
		segmentLabels?: boolean;
		tooltip?: boolean;
	} = {}
): ChartConfiguration<'pie'> {
	return {
		type: 'pie',
		data: {
			labels,
			datasets: [
				{
					data: values,
					backgroundColor: colors,
					borderWidth: 0,
					hoverOffset: 0
				}
			]
		},
		options: {
			responsive: true,
			maintainAspectRatio: false,
			plugins: {
				legend: { display: false },
				tooltip: options.tooltip
					? { displayColors: false }
					: { enabled: false },
				segmentLabels: {
					display: options.segmentLabels === true,
					color: '#FFFFFF',
					fontFamily: 'Gilroy',
					fontSize: 20,
					fontWeight: '700',
					minPercentageToShow: 1
				}
			},
			animation: false
		} as any
	};
}

export function buildPreviewPolarAreaChartConfig(
	labels: string[],
	values: number[],
	colors: string[]
): ChartConfiguration<'polarArea'> {
	const labelCount = labels.length || 1;
	return {
		type: 'polarArea',
		data: {
			labels,
			datasets: [
				{
					data: values,
					backgroundColor: colors,
					borderWidth: 0
				}
			]
		},
		options: {
			responsive: true,
			maintainAspectRatio: false,
			layout: {
				padding: 40
			},
			plugins: {
				legend: { display: false },
				tooltip: {
					callbacks: {
						label: (context: any) =>
							` ${context.label}: ${context.parsed.r}`
					}
				},
				polarAreaLabels: {
					display: true,
					color: '#000000',
					fontFamily: 'Inter, sans-serif',
					fontSize: 12,
					fontWeight: '700',
					labelRadiusOffset: 22,
					spokeColor: '#E8EDF5',
					spokeWidth: 1.5
				}
			},
			scales: {
				r: {
					startAngle: -180 / labelCount,
					grid: { color: '#E7EEF9' },
					ticks: { display: false },
					pointLabels: { display: false }
				}
			},
			animation: false
		} as any
	};
}

export function buildPreviewStackedPercentBarChartConfig(
	labels: string[],
	datasets: ReportCategoricalDataset[]
): ChartConfiguration<'bar'> {
	const normalized = datasets.map(dataset => ({
		...dataset,
		data: dataset.data.map((value, index) => {
			const total = datasets.reduce(
				(sum, item) => sum + (item.data[index] || 0),
				0
			);
			return total > 0 ? (value / total) * 100 : 0;
		})
	}));

	return {
		type: 'bar',
		data: {
			labels,
			datasets: normalized.map(dataset => ({
				label: dataset.label,
				data: dataset.data,
				backgroundColor: dataset.color,
				borderSkipped: false,
				borderRadius: 0,
				barPercentage: 0.72,
				categoryPercentage: 0.72
			}))
		},
		options: {
			responsive: true,
			maintainAspectRatio: false,
			animation: false,
			plugins: {
				legend: { display: false },
				tooltip: { displayColors: false }
			},
			scales: {
				x: {
					stacked: true,
					grid: { display: false },
					border: { display: false },
					ticks: {
						color: '#7E8A9A',
						maxRotation: 46,
						minRotation: 46,
						font: { family: 'Gilroy', size: 11 }
					}
				},
				y: {
					stacked: true,
					beginAtZero: true,
					max: 100,
					grid: { color: '#E7EEF9' },
					border: { display: false },
					ticks: {
						stepSize: 20,
						color: '#9FB0C1',
						font: { family: 'Gilroy', size: 11 },
						callback: value => `${value}`
					}
				}
			}
		}
	};
}

export function buildPreviewValueBarChartConfig(
	labels: string[],
	datasets: ReportCategoricalDataset[]
): ChartConfiguration<'bar'> {
	const maxValue = Math.max(1, ...datasets.flatMap(dataset => dataset.data));
	return {
		type: 'bar',
		data: {
			labels,
			datasets: datasets.map(dataset => ({
				label: dataset.label,
				data: dataset.data,
				backgroundColor: dataset.color,
				borderColor: 'transparent',
				borderWidth: 0,
				borderRadius: 0,
				borderSkipped: false,
				barPercentage: 0.56,
				categoryPercentage: 0.72
			}))
		},
		options: {
			responsive: true,
			maintainAspectRatio: false,
			layout: {
				padding: {
					top: 22
				}
			},
			plugins: {
				legend: { display: false },
				tooltip: { displayColors: false },
				barValueLabels: {
					display: true,
					color: '#0F121A',
					fontFamily: 'Gilroy',
					fontSize: 11,
					fontWeight: '700',
					format: 'compact'
				}
			},
			scales: {
				x: {
					stacked: datasets.length > 1,
					grid: { display: false, drawBorder: false },
					border: { display: false },
					ticks: { display: false }
				},
				y: {
					stacked: datasets.length > 1,
					beginAtZero: true,
					suggestedMax: Math.ceil(maxValue * 1.16),
					grid: { display: false },
					border: { display: false },
					ticks: {
						color: '#8DA6B6',
						font: { family: 'Gilroy', size: 11 },
						callback: (value: string | number) =>
							formatCompactNumber(Number(value))
					}
				}
			},
			animation: false
		} as any
	};
}

/**
 * Give a filled line dataset the fade under it that its own line colour asks
 * for.
 *
 * A slide that hard-codes the gradient loses it the moment the slide is
 * cloned — `applyOverrides` clones through JSON, and a function-valued
 * `backgroundColor` does not survive that, so one saved text edit was enough
 * to leave the area chart flat. Deriving it here instead means the fill
 * follows whatever colour the line ended up, including the report's theme,
 * and survives every clone because it is rebuilt rather than carried.
 */
export function restoreAreaFills(config: ChartConfiguration): void {
	const datasets = (config?.data?.datasets ?? []) as Array<
		Record<string, unknown>
	>;
	for (const dataset of datasets) {
		if (!dataset || !dataset['fill'] || dataset['backgroundColor']) {
			continue;
		}
		const line = dataset['borderColor'];
		if (typeof line !== 'string') {
			continue;
		}
		dataset['backgroundColor'] = (context: { chart: any }) => {
			const { ctx, chartArea } = context.chart;
			if (!chartArea) {
				return translucent(line, 0);
			}
			const gradient = ctx.createLinearGradient(
				0,
				chartArea.top,
				0,
				chartArea.bottom
			);
			gradient.addColorStop(0, translucent(line, 0.18));
			gradient.addColorStop(1, translucent(line, 0));
			return gradient;
		};
	}
}

/** `#rrggbb` or an rgb()/rgba() string to the same colour at `alpha`. */
function translucent(color: string, alpha: number): string {
	const hex = color.trim().replace('#', '');
	if (/^[0-9a-f]{6}$/i.test(hex)) {
		const value = parseInt(hex, 16);
		// eslint-disable-next-line no-bitwise
		return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${
			// eslint-disable-next-line no-bitwise
			value & 255
		}, ${alpha})`;
	}
	const parts = color.match(/-?\d+(\.\d+)?/g);
	return parts && parts.length >= 3
		? `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${alpha})`
		: color;
}

/**
 * Draw a point when there are too few of them to draw a line.
 *
 * The time-series slides hide their points (`pointRadius: 0`) because a line
 * is the thing to read. A series with one point has no line to draw, so it
 * rendered as an empty frame — a week holding one day of data looked exactly
 * like a broken chart. A dot at the one value it has is the honest picture,
 * and the reader can hover it for the number.
 */
export function showLonePoints(config: ChartConfiguration): void {
	const datasets = (config?.data?.datasets ?? []) as Array<
		Record<string, unknown>
	>;
	for (const dataset of datasets) {
		const values = Array.isArray(dataset['data'])
			? (dataset['data'] as unknown[])
			: [];
		const present = values.filter(
			value => value !== null && value !== undefined
		).length;
		if (!present || present >= 2) {
			continue;
		}
		dataset['pointRadius'] = 4;
		dataset['pointHoverRadius'] = 5;
		if (typeof dataset['borderColor'] === 'string') {
			dataset['pointBackgroundColor'] = dataset['borderColor'];
			dataset['pointBorderColor'] = dataset['borderColor'];
		}
	}
}

/** How many of a dataset's values are actually there. */
function presentPoints(dataset: Record<string, unknown>): number {
	const values = Array.isArray(dataset['data'])
		? (dataset['data'] as unknown[])
		: [];
	return values.filter(value => value !== null && value !== undefined).length;
}

/**
 * Draw a time series that is too short for a line as bars instead.
 *
 * A line needs two points. A report whose period holds one day of data has
 * one, and whatever a line chart does with it looks broken: an empty frame,
 * or a lone dot in a corner. A bar per series for that day is the picture the
 * data can actually support — volume becomes one column, sentiment three side
 * by side, emotions one stacked column — and it reads as meant, not as a
 * failure. A chart where at least one series has a real line stays a line;
 * `showLonePoints` looks after any short series inside it.
 *
 * Returns true when it switched, so a caller holding its own chart type can
 * follow.
 */
export function drawSparseSeriesAsBars(config: ChartConfiguration): boolean {
	const chart = config as unknown as Record<string, any>;
	if (chart?.['type'] !== 'line') {
		return false;
	}
	const datasets = (chart['data']?.datasets ?? []) as Array<
		Record<string, unknown>
	>;
	const longest = Math.max(0, ...datasets.map(presentPoints));
	// Nothing at all is the slide's own empty state; two or more is a line.
	if (longest === 0 || longest >= 2) {
		return false;
	}

	chart['type'] = 'bar';
	for (const dataset of datasets) {
		const colour = dataset['borderColor'];
		// A dataset may pin its own type, and in a bar chart it would still be
		// drawn as a line.
		dataset['type'] = 'bar';
		// A fade meant for the area under a line would leave a bar see-through.
		if (typeof colour === 'string') {
			dataset['backgroundColor'] = colour;
		}
		dataset['borderWidth'] = 0;
		dataset['borderRadius'] = 6;
		dataset['maxBarThickness'] = 56;
		for (const key of [
			'fill',
			'tension',
			'pointRadius',
			'pointHoverRadius'
		]) {
			delete dataset[key];
		}
	}

	const options = (chart['options'] ??= {});
	const scales = (options['scales'] ??= {});
	// A line chart pins its first point to the axis; a lone bar there would
	// be cut in half.
	(scales['x'] ??= {})['offset'] = true;
	return true;
}

/**
 * Everything a time-series chart needs done to it just before it is drawn,
 * in the order it has to happen, and the type it ended up as.
 *
 * Done at draw time rather than when the slide is built because the slide is
 * cloned through JSON whenever an edit is applied, and nothing computed here
 * would survive that — the area fade is a function. Kept in one place because
 * the order matters: a series switched to bars must not then get an area fade
 * or a lone point.
 */
export function prepareForDrawing(config: ChartConfiguration): string {
	if (!drawSparseSeriesAsBars(config)) {
		restoreAreaFills(config);
		showLonePoints(config);
	}
	return String((config as unknown as { type?: string })?.type ?? 'line');
}
